import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  OVERALL_STATUS,
  SERVICE_STATUS,
  type OverallStatus,
  type ServiceStatus,
} from '@/constants/serviceHealth';

const POLL_INTERVAL_MS = 60 * 1000;

export type { OverallStatus };

export interface ServiceHealthEntry {
  status: ServiceStatus;
  responseTimeMs: number;
}

export interface ServiceHealthState {
  services: Record<string, ServiceHealthEntry>;
  overall: OverallStatus;
}

interface HealthStatusApiResponse {
  services: {
    registry: ServiceHealthEntry;
    search: ServiceHealthEntry;
    searchGraphql: ServiceHealthEntry;
    auth: ServiceHealthEntry;
  };
}

function computeOverall(
  services: Record<string, ServiceHealthEntry>,
): OverallStatus {
  const entries = Object.values(services);
  if (entries.length === 0) return OVERALL_STATUS.LOADING;
  const healthyCount = entries.filter(
    (e) => e.status === SERVICE_STATUS.HEALTHY,
  ).length;
  if (healthyCount === entries.length) return OVERALL_STATUS.HEALTHY;
  if (healthyCount === 0) return OVERALL_STATUS.UNHEALTHY;
  return OVERALL_STATUS.DEGRADED;
}

export function useServiceHealth(): ServiceHealthState {
  const [state, setState] = useState<ServiceHealthState>({
    services: {},
    overall: OVERALL_STATUS.LOADING,
  });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchHealth = () => {
    axios
      .get<HealthStatusApiResponse>('/api/health-status', { timeout: 15000 })
      .then((res) => {
        const services = res.data.services as Record<
          string,
          ServiceHealthEntry
        >;
        setState({
          services,
          overall: computeOverall(services),
        });
      })
      .catch(() => {
        setState((prev) => ({
          ...prev,
          overall: OVERALL_STATUS.UNHEALTHY,
          services: prev.services,
        }));
      });
  };

  useEffect(() => {
    fetchHealth();
    intervalRef.current = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return state;
}
